/**
 * Enemy bullet motion recipes (docs/02 B3): curving, accelerate / brake
 * (optionally braking to a stop and RUSHING on), and split-on-timer. Pure
 * data + step math; the pooled bullets carry the state, so nothing here
 * allocates during a fight.
 */

const DEG = Math.PI / 180;
/** Below this speed (px/s) a braked bullet keeps its last heading */
const STOPPED = 1e-3;

/** A bullet that bursts into a small fan or ring after a delay. */
export interface SplitDef {
  /** Seconds after spawn */
  after: number;
  count: number;
  /** Fan width centered on the travel direction (deg); 360 = full ring */
  spreadDeg: number;
  speed: number;
  /** Child look/hitbox — defaults to the parent's */
  bullet?: string;
  radius?: number;
  damage?: number;
  /** Child lifetime (s) */
  life?: number;
}

/** Optional motion on top of the straight flight (DSL `motion` field). */
export interface BulletMotion {
  /** Angular velocity (deg/s): the path curls; + turns clockwise on screen */
  curveDeg?: number;
  /** Mirror the curl on every other bullet of a volley (fans curl both ways) */
  curveAlternate?: boolean;
  /** Acceleration along travel (px/s²); negative brakes */
  accel?: number;
  /** Speed clamp while accelerating / braking (px/s) */
  minSpeed?: number;
  maxSpeed?: number;
  /** After this long (s) the acceleration switches to `rushAccel` */
  rushAt?: number;
  rushAccel?: number;
  split?: SplitDef;
}

/** Motion state carried by every pooled bullet (reset on spawn). */
export interface MotionBody {
  vx: number;
  vy: number;
  /** Last non-zero heading — a bullet braked to a stop keeps it */
  hx: number;
  hy: number;
  age: number;
  curve: number;
  accel: number;
  minSpeed: number;
  maxSpeed: number;
  rushAt: number;
  rushAccel: number;
  split: SplitDef | null;
}

/** Load a recipe onto a freshly spawned bullet. @param sign ±1 curl mirror */
export function applyMotion(b: MotionBody, m: BulletMotion | undefined, sign: number): boolean {
  b.age = 0;
  b.curve = (m?.curveDeg ?? 0) * DEG * sign;
  b.accel = m?.accel ?? 0;
  b.minSpeed = m?.minSpeed ?? 0;
  b.maxSpeed = m?.maxSpeed ?? Infinity;
  b.rushAt = m?.rushAt ?? -1;
  b.rushAccel = m?.rushAccel ?? 0;
  b.split = m?.split ?? null;
  return m !== undefined;
}

/** One step of curve + accel. @returns true when the bullet is due to split */
export function stepMotion(b: MotionBody, dt: number): boolean {
  b.age += dt;
  if (b.rushAt >= 0 && b.age >= b.rushAt) {
    b.accel = b.rushAccel;
    b.rushAt = -1;
  }
  if (b.curve !== 0) {
    const c = Math.cos(b.curve * dt);
    const s = Math.sin(b.curve * dt);
    const vx = b.vx * c - b.vy * s;
    b.vy = b.vx * s + b.vy * c;
    b.vx = vx;
  }
  const sp = Math.hypot(b.vx, b.vy);
  if (sp > STOPPED) {
    b.hx = b.vx / sp;
    b.hy = b.vy / sp;
  }
  if (b.accel !== 0) {
    const next = Math.min(b.maxSpeed, Math.max(b.minSpeed, sp + b.accel * dt));
    b.vx = b.hx * next;
    b.vy = b.hy * next;
  }
  return b.split !== null && b.age >= b.split.after;
}

/** Speeds in a recipe scaled by the tier bullet-speed ramp (built per phase). */
export function scaleMotion(m: BulletMotion | undefined, mul: number): BulletMotion | undefined {
  if (!m) return undefined;
  return {
    ...m,
    accel: m.accel !== undefined ? m.accel * mul : undefined,
    minSpeed: m.minSpeed !== undefined ? m.minSpeed * mul : undefined,
    maxSpeed: m.maxSpeed !== undefined ? m.maxSpeed * mul : undefined,
    rushAccel: m.rushAccel !== undefined ? m.rushAccel * mul : undefined,
    split: m.split ? { ...m.split, speed: m.split.speed * mul } : undefined,
  };
}

/** Child heading k of n for a split burst around the parent's heading. */
export function splitAngle(heading: number, s: SplitDef, k: number): number {
  if (s.spreadDeg >= 360) return heading + (Math.PI * 2 * k) / s.count;
  const half = (s.spreadDeg / 2) * DEG;
  const t = s.count === 1 ? 0.5 : k / (s.count - 1);
  return heading - half + t * half * 2;
}
